import { createBrowserHistory } from 'history';
import { isNull, isUndefined, get, omit } from 'lodash';
import { flow, omitBy } from 'lodash/fp';
import queryString from 'query-string';

import SHOPS, { DEV_ENVIRONMENT_SUBDOMAINS } from 'constants/shops';
import Settings from 'utils/Settings';

export const history = createBrowserHistory();

export const utils = {
  getWindowLocation: () => window.location,
};

/**
 * Returns the origin of the current window location
 */
export const getLocationOrigin = () => utils.getWindowLocation().origin;

/**
 * Returns an object from a url query string
 */
export const parseQueryString = (search = utils.getWindowLocation().search) =>
  queryString.parse(search);

/**
 * Builds a url query string from an object. Rips out null/undefined values.
 */
export const stringifyQueryString = obj => {
  // Trim out null values
  const query = flow(omitBy(isNull), omitBy(isUndefined))(obj);

  return queryString.stringify(query);
};

export const queryParamSearchQuery = () => {
  const currentUrlParamsInit = parseQueryString();
  const query = get(currentUrlParamsInit, 'query');
  return query;
};

/**
 * Replace current window location with new path, params, state
 */
export const replaceHistory = (
  queryParams,
  state,
  path = utils.getWindowLocation().pathname,
  hash = utils.getWindowLocation().hash
) => {
  const query = stringifyQueryString(queryParams);
  history.replace(`${path}${query ? `?${query}` : ''}${hash}`, state);
};

export const removeUrlParam = urlParam => {
  const urlParams = parseQueryString();
  const cleanedParams = omit(urlParams, urlParam);
  replaceHistory(cleanedParams);
};

export const generateProductUrl = (handle, params = {}) => {
  const url = `/products/${handle}`;
  const query = queryString.stringify(params);

  return query.length ? `${url}?${query}` : url;
};

export const generateSearchUrl = (queryToSearch, params = {}) => {
  if (queryToSearch) {
    Object.assign(params, { query: queryToSearch });
  }

  const queryParamsStringified = queryString.stringify(params);
  const url = `/pages/search`;

  return queryParamsStringified.length ? `${url}?${queryParamsStringified}` : url;
};

export const navigateToUrl = url => {
  window.location.assign(url);
};

export const replaceSubdomain = (url, subdomain) => {
  const protocolSeparator = '://';

  // Get current shop url
  const shopUrl = get(SHOPS, [Settings.shopID, 'url']);
  // Extracts domain only from current shop (i.e. removes 'https://www.' and leaves 'allbirds.com')
  // This step is necessary because there are some domains with TLDs that are multi-part (co.uk, com.au)
  const domain = shopUrl.substring(shopUrl.indexOf('.') + 1);
  // Extracts only subdomains (multiple levels supported) of current url (i.e. 'fr-fr.subdomain-2.subdomain-3.')
  const subdomains = url.slice(
    url.indexOf(protocolSeparator) + protocolSeparator.length,
    url.indexOf(domain)
  );
  // Get first subdomain as we'll be replacing that one
  const firstSubdomain = subdomains.split('.')[0];
  // Set newSubdomain to 'www' if the shopLocale is the subdomain
  const newSubdomain = subdomain === Settings.shopLocale ? 'www' : subdomain;

  // If the first subdomain exists, replace the first subdomain with the new subdomain value
  if (firstSubdomain && !DEV_ENVIRONMENT_SUBDOMAINS.includes(firstSubdomain)) {
    return url.replace(newSubdomain ? firstSubdomain : `${firstSubdomain}.`, newSubdomain);
  }
  // Else insert the new subdomain in the url
  const protocol = url.slice(0, url.indexOf(protocolSeparator));
  return `${protocol}${protocolSeparator}${newSubdomain}${newSubdomain ? '.' : ''}${url.substring(
    `${protocol}${protocolSeparator}`.length
  )}`;
};

export const getCurrentDomain = () => {
  // Get current shop url
  const url = utils.getWindowLocation().hostname;
  // Extracts domain only from current shop (i.e. removes 'www.' and leaves '.allbirds.com')
  // This step is necessary because there are some domains with TLDs that are multi-part (co.uk, com.au)
  let domain = url.substring(url.indexOf('.'));

  // if there's no subdomain in the current window location, prepend the domain with
  // a period to include any subdomains that are navigated to
  if (domain.charAt(0) !== '.') {
    domain = `.${domain}`;
  }

  return domain;
};
